import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  useGetFieldInspectionRequestsQuery,
  useGetFieldSitesQuery,
  useGetKfoRfiQuery,
  useListGeoSchemesQuery,
  useListPtoExecutiveActsQuery,
  usePatchKfoRfiMutation,
  type KfoControlKind,
  type KfoRfi,
  type KfoRfiSource,
  type KfoRfiStatus,
  type PtoExecutiveAct,
} from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import {
  Actions,
  Banner,
  CONTROL_KIND_LABEL,
  DataTable,
  Dialog,
  Field,
  Filters,
  Muted,
  Pager,
  RFI_STATUS_LABEL,
  SOURCE_LABEL,
  SmallInput,
  SmallSelect,
  TableWrap,
  Tag,
  errorText,
  isNotFound,
  fmtDate,
  fmtNum,
} from './ui';

const MobileCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const MobileRfiCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MobileRfiHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const MobileRfiTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MobileRfiMeta = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.45;
`;

const MobileActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const STATUS_TONE: Record<KfoRfiStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  draft: 'neutral',
  submitted: 'info',
  accepted: 'success',
  rejected: 'danger',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function actLabel(a: PtoExecutiveAct): string {
  const no = a.act_no ? `№ ${a.act_no}` : 'б/н';
  const date = a.act_date ? ` от ${fmtDate(a.act_date)}` : '';
  return `${(a.kind || 'акт').toUpperCase()} ${no}${date} — ${a.work_name}`;
}

/**
 * Журнал RFI: предъявления Заказчику из среза (source = Заказчик) и наши,
 * созданные выпуском «Приложения к RFI» (source = ОСНОВА).
 */
export function KfoRfiTab({
  projectId,
  canWrite,
  onOpenLine,
  mobileMode = false,
  onToggleMobileMode,
}: {
  projectId: string;
  canWrite: boolean;
  onOpenLine: (lineId: string) => void;
  mobileMode?: boolean;
  onToggleMobileMode?: () => void;
}) {
  const [controlKind, setControlKind] = useState<KfoControlKind | ''>('');
  const [source, setSource] = useState<KfoRfiSource | ''>('');
  const [status, setStatus] = useState<KfoRfiStatus | ''>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [linking, setLinking] = useState<KfoRfi | null>(null);
  const [quickErr, setQuickErr] = useState<string | null>(null);
  const dq = useDebouncedValue(q.trim(), 400);
  const [patchRfi, { isLoading: patchingRfi }] = usePatchKfoRfiMutation();

  const { data, isFetching, error } = useGetKfoRfiQuery(
    {
      projectId,
      control_kind: controlKind,
      source,
      status,
      q: dq || undefined,
      page: page + 1,
      per_page: perPage,
    },
    { skip: !projectId },
  );
  const { data: actsData } = useListPtoExecutiveActsQuery({ projectId }, { skip: !projectId });
  const actsById = useMemo(
    () => new Map((actsData?.items ?? []).map((a) => [a.id, a])),
    [actsData],
  );
  const { data: geoSchemesData } = useListGeoSchemesQuery(
    { project_id: projectId, limit: 200 },
    { skip: !projectId },
  );
  const geoSchemesById = useMemo(
    () => new Map((geoSchemesData?.schemes ?? []).map((s) => [s.id, s])),
    [geoSchemesData],
  );

  const rows = data?.items ?? [];
  const hasFilters = controlKind || source || status || q;

  async function handleQuickSignOff(r: KfoRfi, nextStatus: KfoRfiStatus) {
    setQuickErr(null);
    try {
      await patchRfi({ id: r.id, status: nextStatus }).unwrap();
    } catch (e) {
      setQuickErr(errorText(e, 'Не удалось обновить статус RFI.'));
    }
  }

  return (
    <div>
      <Filters>
        <Field>
          Вид контроля
          <SmallSelect
            value={controlKind}
            onChange={(e) => {
              setControlKind(e.target.value as KfoControlKind | '');
              setPage(0);
            }}
          >
            <option value="">все</option>
            {(Object.keys(CONTROL_KIND_LABEL) as KfoControlKind[]).map((k) => (
              <option key={k} value={k}>
                {CONTROL_KIND_LABEL[k]}
              </option>
            ))}
          </SmallSelect>
        </Field>
        <Field>
          Источник
          <SmallSelect
            value={source}
            onChange={(e) => {
              setSource(e.target.value as KfoRfiSource | '');
              setPage(0);
            }}
          >
            <option value="">все</option>
            <option value="customer">{SOURCE_LABEL.customer}</option>
            <option value="osnova">{SOURCE_LABEL.osnova}</option>
          </SmallSelect>
        </Field>
        <Field>
          Статус
          <SmallSelect
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as KfoRfiStatus | '');
              setPage(0);
            }}
          >
            <option value="">все</option>
            {(Object.keys(RFI_STATUS_LABEL) as KfoRfiStatus[]).map((s) => (
              <option key={s} value={s}>
                {RFI_STATUS_LABEL[s]}
              </option>
            ))}
          </SmallSelect>
        </Field>
        <Field style={{ flex: '1 1 220px' }}>
          Номер RFI или Ключ
          <SmallInput
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="128217, СКМ-4511…, Ключ"
          />
        </Field>
        {onToggleMobileMode && (
          <Button
            $size="sm"
            $variant={mobileMode ? 'primary' : 'secondary'}
            type="button"
            onClick={onToggleMobileMode}
          >
            {mobileMode ? 'Табличный режим' : 'Карточки СК/ГК (площадка)'}
          </Button>
        )}
        {hasFilters && (
          <Button
            $size="sm"
            $variant="ghost"
            type="button"
            onClick={() => {
              setControlKind('');
              setSource('');
              setStatus('');
              setQ('');
              setPage(0);
            }}
          >
            Сбросить
          </Button>
        )}
      </Filters>

      {error && !isNotFound(error) ? (
        <Banner $tone="danger">{errorText(error, 'Не удалось загрузить журнал RFI.')}</Banner>
      ) : null}
      {quickErr && <Banner $tone="danger">{quickErr}</Banner>}

      {mobileMode ? (
        <MobileCardsGrid style={{ opacity: isFetching ? 0.6 : 1 }}>
          {rows.length === 0 && (
            <Muted>{isFetching ? 'Загрузка…' : 'Записей RFI нет.'}</Muted>
          )}
          {rows.map((r) => {
            const act = r.executive_act_id ? actsById.get(r.executive_act_id) : undefined;
            const geoScheme = r.geodesy_scheme_id
              ? geoSchemesById.get(r.geodesy_scheme_id)
              : undefined;
            const canQuickSign = canWrite && r.source === 'osnova';
            return (
              <MobileRfiCard key={r.id}>
                <MobileRfiHeader>
                  <MobileRfiTitle>
                    {r.rfi_no_po || (r.rfi_no ? `RFI № ${r.rfi_no}` : `Ключ ${r.ext_key ?? '—'}`)}
                  </MobileRfiTitle>
                  <Tag $tone={STATUS_TONE[r.status] ?? 'neutral'}>
                    {RFI_STATUS_LABEL[r.status] ?? r.status}
                  </Tag>
                </MobileRfiHeader>
                <MobileRfiMeta>
                  <div>
                    <strong>Контроль:</strong> {CONTROL_KIND_LABEL[r.control_kind] ?? r.control_kind} ·{' '}
                    <strong>Дата:</strong> {fmtDate(r.held_on)}
                  </div>
                  <div>
                    <strong>Элемент:</strong> {r.element || '—'} · <strong>Объём:</strong>{' '}
                    {fmtNum(r.qty) || '—'}
                  </div>
                  <div>
                    <strong>ЕР:</strong> {r.ep_code || '—'} · <strong>РД:</strong>{' '}
                    {r.rd_sheet_code || '—'}
                    {r.rd_revision ? ` (рев. ${r.rd_revision})` : ''}
                  </div>
                  {(act || geoScheme || r.inspection_request_id) && (
                    <div style={{ marginTop: 4 }}>
                      {r.inspection_request_id && <Tag $tone="info">заявка</Tag>}{' '}
                      {act && <Tag $tone="success">акт {act.act_no || 'б/н'}</Tag>}{' '}
                      {geoScheme && (
                        <Tag $tone="info">гео-ИС {geoScheme.number || geoScheme.title}</Tag>
                      )}
                    </div>
                  )}
                </MobileRfiMeta>
                <MobileActionsRow>
                  {canQuickSign && r.status !== 'accepted' && (
                    <Button
                      $size="sm"
                      $variant="primary"
                      type="button"
                      disabled={patchingRfi}
                      onClick={() => void handleQuickSignOff(r, 'accepted')}
                    >
                      Принять (СК/ГК)
                    </Button>
                  )}
                  {canQuickSign && r.status !== 'rejected' && (
                    <Button
                      $size="sm"
                      $variant="secondary"
                      type="button"
                      disabled={patchingRfi}
                      onClick={() => void handleQuickSignOff(r, 'rejected')}
                    >
                      Замечания
                    </Button>
                  )}
                  {canWrite && (
                    <Button
                      $size="sm"
                      $variant="ghost"
                      type="button"
                      onClick={() => setLinking(r)}
                    >
                      Связи ИД / Гео
                    </Button>
                  )}
                  {r.line_id && (
                    <Button
                      $size="sm"
                      $variant="ghost"
                      type="button"
                      onClick={() => onOpenLine(r.line_id!)}
                    >
                      Строка КФО →
                    </Button>
                  )}
                </MobileActionsRow>
              </MobileRfiCard>
            );
          })}
        </MobileCardsGrid>
      ) : (
        <TableWrap style={{ opacity: isFetching ? 0.6 : 1 }}>
          <DataTable>
            <thead>
              <tr>
                <th>Дата</th>
                <th>№ RFI</th>
                <th>№ RFI ПО</th>
                <th>Контроль</th>
                <th>Ключ</th>
                <th>Шифр ЕР</th>
                <th>Лист РД / рев.</th>
                <th>Элемент</th>
                <th className="num">Объём</th>
                <th>Источник</th>
                <th>Статус</th>
                <th>Заявка / акт ИД / ИС</th>
                {canWrite && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: 28 }}>
                    <Muted>{isFetching ? 'Загрузка…' : 'Записей нет.'}</Muted>
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const act = r.executive_act_id ? actsById.get(r.executive_act_id) : undefined;
                const geoScheme = r.geodesy_scheme_id
                  ? geoSchemesById.get(r.geodesy_scheme_id)
                  : undefined;
                return (
                  <tr
                    key={r.id}
                    data-click={r.line_id ? 'true' : undefined}
                    data-tone={r.excluded ? 'muted' : undefined}
                    onClick={() => r.line_id && onOpenLine(r.line_id)}
                  >
                    <td className="nowrap">{fmtDate(r.held_on)}</td>
                    <td className="nowrap">{r.rfi_no ?? ''}</td>
                    <td className="nowrap">{r.rfi_no_po}</td>
                    <td>{CONTROL_KIND_LABEL[r.control_kind] ?? r.control_kind}</td>
                    <td className="nowrap">{r.ext_key ?? ''}</td>
                    <td className="nowrap">{r.ep_code}</td>
                    <td className="nowrap">
                      {r.rd_sheet_code}
                      {r.rd_revision ? <Muted as="span"> · рев. {r.rd_revision}</Muted> : null}
                    </td>
                    <td className="wrap" style={{ minWidth: 140 }}>
                      {r.element}
                    </td>
                    <td className="num">{fmtNum(r.qty)}</td>
                    <td>{SOURCE_LABEL[r.source] ?? r.source}</td>
                    <td>
                      <Tag $tone={STATUS_TONE[r.status] ?? 'neutral'}>
                        {RFI_STATUS_LABEL[r.status] ?? r.status}
                      </Tag>
                      {r.excluded && (
                        <>
                          {' '}
                          <Tag title="Исключено Заказчиком">исключён</Tag>
                        </>
                      )}
                    </td>
                    <td>
                      {r.inspection_request_id && <Tag $tone="info">заявка</Tag>}{' '}
                      {r.executive_act_id && (
                        <Tag $tone="success" title={act ? actLabel(act) : r.executive_act_id}>
                          акт{act?.act_no ? ` ${act.act_no}` : ''}
                        </Tag>
                      )}{' '}
                      {r.geodesy_scheme_id && (
                        <Tag
                          $tone="info"
                          title={
                            geoScheme
                              ? `${geoScheme.title}${geoScheme.number ? ` № ${geoScheme.number}` : ''}`
                              : r.geodesy_scheme_id
                          }
                        >
                          гео-ИС{geoScheme?.number ? ` ${geoScheme.number}` : ''}
                        </Tag>
                      )}
                    </td>
                    {canWrite && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button
                          $size="sm"
                          $variant="ghost"
                          type="button"
                          onClick={() => setLinking(r)}
                        >
                          Связи
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
        </TableWrap>
      )}
      <Pager
        page={page}
        perPage={perPage}
        total={data?.total ?? 0}
        unit="записей"
        onPage={setPage}
        onPerPage={(n) => {
          setPerPage(n);
          setPage(0);
        }}
      />

      {linking && (
        <RfiLinksDialog
          projectId={projectId}
          rfi={linking}
          acts={actsData?.items ?? []}
          onClose={() => setLinking(null)}
        />
      )}
    </div>
  );
}

/**
 * Ручные связи RFI: заявка на освидетельствование (Прораб), акт ИД и
 * исполнительная геодезическая схема; для наших RFI — ещё и статус.
 */
function RfiLinksDialog({
  projectId,
  rfi,
  acts,
  onClose,
}: {
  projectId: string;
  rfi: KfoRfi;
  acts: PtoExecutiveAct[];
  onClose: () => void;
}) {
  const [patch, { isLoading }] = usePatchKfoRfiMutation();
  const [actId, setActId] = useState(rfi.executive_act_id ?? '');
  const [requestId, setRequestId] = useState(rfi.inspection_request_id ?? '');
  const [geoSchemeId, setGeoSchemeId] = useState(rfi.geodesy_scheme_id ?? '');
  const [status, setStatus] = useState<KfoRfiStatus>(rfi.status);
  const [siteId, setSiteId] = useState('');
  const [manual, setManual] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: sitesData, error: sitesError } = useGetFieldSitesQuery({ projectId });
  const sites = sitesData?.sites ?? [];
  const { data: reqData, isFetching: reqLoading } = useGetFieldInspectionRequestsQuery(siteId, {
    skip: !siteId,
  });
  const requests = reqData?.requests ?? [];
  const { data: geoSchemesData } = useListGeoSchemesQuery(
    { project_id: projectId, limit: 200 },
    { skip: !projectId },
  );
  const geoSchemes = geoSchemesData?.schemes ?? [];
  // Нет площадок Прораба (или модуль выключен) — id заявки вводится руками.
  const pickerAvailable = !sitesError && sites.length > 0 && !manual;

  const isOsnova = rfi.source === 'osnova';
  const title =
    rfi.rfi_no_po || (rfi.rfi_no ? `RFI ${rfi.rfi_no}` : `RFI по Ключу ${rfi.ext_key ?? ''}`);

  async function onSave() {
    setError(null);
    const req = requestId.trim();
    if (req && !UUID_RE.test(req)) {
      setError('Идентификатор заявки должен быть UUID (или оставьте поле пустым).');
      return;
    }
    const geo = geoSchemeId.trim();
    if (geo && !UUID_RE.test(geo)) {
      setError('Идентификатор гео-схемы должен быть UUID (или оставьте поле пустым).');
      return;
    }
    const body: {
      id: string;
      inspection_request_id?: string | null;
      executive_act_id?: string | null;
      geodesy_scheme_id?: string | null;
      status?: KfoRfiStatus;
    } = { id: rfi.id };
    if ((rfi.executive_act_id ?? '') !== actId) body.executive_act_id = actId || null;
    if ((rfi.inspection_request_id ?? '') !== req) body.inspection_request_id = req || null;
    if ((rfi.geodesy_scheme_id ?? '') !== geo) body.geodesy_scheme_id = geo || null;
    if (isOsnova && status !== rfi.status) body.status = status;
    if (Object.keys(body).length === 1) {
      onClose();
      return;
    }
    try {
      await patch(body).unwrap();
      onClose();
    } catch (e) {
      setError(errorText(e, 'Не удалось сохранить связи.'));
    }
  }

  return (
    <Dialog open onClose={onClose} title={`Связи: ${title}`} width={640}>
      <Muted style={{ marginBottom: 12 }}>
        Ключ {rfi.ext_key ?? '—'} · {CONTROL_KIND_LABEL[rfi.control_kind]} · объём{' '}
        {fmtNum(rfi.qty) || '—'} · {SOURCE_LABEL[rfi.source]}
      </Muted>

      <Field>
        Акт ИД (АОСР и др.)
        <Select value={actId} onChange={(e) => setActId(e.target.value)}>
          <option value="">— не связан —</option>
          {actId && !acts.some((a) => a.id === actId) && (
            <option value={actId}>связан: {actId}</option>
          )}
          {acts.map((a) => (
            <option key={a.id} value={a.id}>
              {actLabel(a)}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        Исполнительная геодезическая схема (Геодезист)
        <Select value={geoSchemeId} onChange={(e) => setGeoSchemeId(e.target.value)}>
          <option value="">— не связана —</option>
          {geoSchemeId && !geoSchemes.some((s) => s.id === geoSchemeId) && (
            <option value={geoSchemeId}>связана: {geoSchemeId}</option>
          )}
          {geoSchemes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.number ? `№ ${s.number} · ` : ''}
              {s.title} · {s.status}
            </option>
          ))}
        </Select>
      </Field>

      <Field as="div">
        Заявка на освидетельствование (Прораб)
        {pickerAvailable ? (
          <>
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">— выберите площадку —</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code ? `${s.code} · ` : ''}
                  {s.name}
                </option>
              ))}
            </Select>
            <Select
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
              disabled={!siteId && !requestId}
            >
              <option value="">— не связана —</option>
              {requestId && !requests.some((r) => r.id === requestId) && (
                <option value={requestId}>связана: {requestId}</option>
              )}
              {requests.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title}
                  {r.period_start ? ` · ${fmtDate(r.period_start)}` : ''} · {r.status}
                </option>
              ))}
            </Select>
            {reqLoading && <Muted>Загрузка заявок…</Muted>}
            <Button
              $size="sm"
              $variant="ghost"
              type="button"
              onClick={() => setManual(true)}
              style={{ alignSelf: 'flex-start' }}
            >
              Ввести id заявки вручную
            </Button>
          </>
        ) : (
          <Input
            value={requestId}
            onChange={(e) => setRequestId(e.target.value)}
            placeholder="UUID заявки (пусто — отвязать)"
          />
        )}
      </Field>

      {isOsnova && (
        <Field>
          Статус нашего RFI
          <Select value={status} onChange={(e) => setStatus(e.target.value as KfoRfiStatus)}>
            {(Object.keys(RFI_STATUS_LABEL) as KfoRfiStatus[]).map((s) => (
              <option key={s} value={s}>
                {RFI_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {error && <Banner $tone="danger">{error}</Banner>}
      <Actions style={{ justifyContent: 'flex-end' }}>
        <Button $variant="secondary" type="button" onClick={onClose}>
          Отмена
        </Button>
        <Button type="button" disabled={isLoading} onClick={onSave}>
          {isLoading ? 'Сохраняю…' : 'Сохранить'}
        </Button>
      </Actions>
    </Dialog>
  );
}
